<template>
    <div class="min-h-screen bg-secondary-50 dark:bg-secondary-900">
        <!-- Simple Header (No Sidebar) -->
        <nav class="bg-white dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700 shadow-sm">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="flex justify-between items-center h-16">
                    <!-- Logo -->
                    <Link href="/dashboard" class="flex items-center space-x-3">
                        <div class="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <span class="text-xl font-semibold text-secondary-900 dark:text-white">
                            Margin Resolve
                        </span>
                    </Link>

                    <!-- Back to My Companies -->
                    <Link
                        href="/my-companies"
                        class="text-sm text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center space-x-1"
                    >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        <span>Back to My Companies</span>
                    </Link>
                </div>
            </div>
        </nav>

        <!-- Main Content -->
        <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <!-- Header -->
            <div class="text-center mb-12">
                <div class="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full mb-4">
                    <svg class="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h1 class="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
                    Reactivate Your Subscription
                </h1>
                <p class="text-lg text-secondary-600 dark:text-secondary-400">
                    Welcome back to <span class="font-semibold">{{ company.name }}</span>
                </p>
            </div>

            <!-- Previous Subscription Info (if exists) -->
            <div v-if="previousSubscription" class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-8">
                <div class="flex items-start space-x-3">
                    <svg class="w-6 h-6 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div class="flex-1">
                        <h3 class="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                            Your Previous Subscription
                        </h3>
                        <div class="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                            <p><strong>Plan:</strong> {{ previousSubscription.plan_name }} ({{ company.currency_symbol }}{{ previousSubscription.plan_price }}/{{ previousSubscription.billing_period }})</p>
                            <p><strong>Cancelled on:</strong> {{ previousSubscription.cancelled_at }}</p>
                            <p v-if="previousAddons.length > 0"><strong>Add-ons:</strong> {{ previousAddons.length }} storage add-on(s)</p>
                        </div>
                        <p class="text-xs text-blue-700 dark:text-blue-400 mt-2">
                            You can choose any plan below. Your previous add-ons are shown for reference.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Plan Selection -->
            <div class="mb-12">
                <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-6">
                    Choose Your Plan
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div
                        v-for="plan in plans"
                        :key="plan.id"
                        @click="selectedPlanId = plan.id"
                        :class="[
                            'relative bg-white dark:bg-secondary-800 rounded-xl border-2 p-6 cursor-pointer transition-all',
                            selectedPlanId === plan.id
                                ? 'border-primary-600 shadow-lg ring-2 ring-primary-600 ring-opacity-50'
                                : 'border-secondary-200 dark:border-secondary-700 hover:border-primary-300 dark:hover:border-primary-700'
                        ]"
                    >
                        <!-- Selected Badge -->
                        <div v-if="selectedPlanId === plan.id" class="absolute top-4 right-4">
                            <div class="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                                <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                </svg>
                            </div>
                        </div>

                        <!-- Plan Name -->
                        <h3 class="text-xl font-bold text-secondary-900 dark:text-white mb-2">
                            {{ plan.name }}
                        </h3>

                        <!-- Price -->
                        <div class="mb-4">
                            <span class="text-3xl font-bold text-primary-600">
                                {{ company.currency_symbol }}{{ plan.price }}
                            </span>
                            <span class="text-secondary-600 dark:text-secondary-400">/{{ plan.billing_period }}</span>
                        </div>

                        <!-- Features -->
                        <ul class="space-y-2 text-sm text-secondary-600 dark:text-secondary-400 mb-4">
                            <li class="flex items-start">
                                <svg class="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                </svg>
                                <span>{{ plan.request_quota }} requests/month</span>
                            </li>
                            <li class="flex items-start">
                                <svg class="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                </svg>
                                <span>{{ plan.storage_quota_gb }} GB storage</span>
                            </li>
                        </ul>

                        <!-- Previous Plan Badge -->
                        <div v-if="previousSubscription && plan.id === previousSubscription.plan_id" class="mt-2">
                            <span class="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                                Previous Plan
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Storage Add-ons -->
            <div class="mb-12">
                <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-2">
                    Additional Storage (Optional)
                </h2>
                <p class="text-sm text-secondary-600 dark:text-secondary-400 mb-6">
                    Add extra storage to your subscription. Billed monthly.
                </p>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div
                        v-for="addon in storageAddons"
                        :key="addon.tier"
                        class="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6"
                    >
                        <!-- Addon Header -->
                        <div class="flex items-start justify-between mb-4">
                            <div>
                                <h3 class="font-semibold text-secondary-900 dark:text-white">
                                    {{ addon.name }}
                                </h3>
                                <p class="text-2xl font-bold text-primary-600 mt-2">
                                    {{ company.currency_symbol }}{{ addon.price }}
                                    <span class="text-sm text-secondary-600 dark:text-secondary-400">/month</span>
                                </p>
                            </div>
                        </div>

                        <!-- Quantity Selector -->
                        <div class="flex items-center space-x-3">
                            <label class="text-sm text-secondary-600 dark:text-secondary-400">Quantity:</label>
                            <div class="flex items-center space-x-2">
                                <button
                                    @click="decrementAddon(addon.tier)"
                                    type="button"
                                    class="w-8 h-8 rounded bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-400 hover:bg-secondary-200 dark:hover:bg-secondary-600 flex items-center justify-center"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                                    </svg>
                                </button>
                                <span class="w-8 text-center font-semibold text-secondary-900 dark:text-white">
                                    {{ getAddonQuantity(addon.tier) }}
                                </span>
                                <button
                                    @click="incrementAddon(addon.tier)"
                                    type="button"
                                    class="w-8 h-8 rounded bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-400 hover:bg-secondary-200 dark:hover:bg-secondary-600 flex items-center justify-center"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v12m6-6H6" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- Previous Addon Badge -->
                        <div v-if="hadPreviousAddon(addon.tier)" class="mt-3">
                            <span class="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                                Previously Used
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Summary & Reactivate -->
            <div class="bg-white dark:bg-secondary-800 rounded-xl border border-secondary-200 dark:border-secondary-700 p-8 shadow-lg">
                <h2 class="text-2xl font-bold text-secondary-900 dark:text-white mb-6">
                    Subscription Summary
                </h2>

                <!-- Selected Plan -->
                <div class="space-y-3 mb-6">
                    <div class="flex justify-between items-center">
                        <span class="text-secondary-700 dark:text-secondary-300">
                            {{ selectedPlan?.name || 'No plan selected' }}
                        </span>
                        <span class="font-semibold text-secondary-900 dark:text-white">
                            {{ company.currency_symbol }}{{ selectedPlan?.price || 0 }}
                        </span>
                    </div>

                    <!-- Storage Add-ons -->
                    <div v-for="addon in selectedStorageAddons" :key="addon.tier" class="flex justify-between items-center text-sm">
                        <span class="text-secondary-600 dark:text-secondary-400">
                            {{ getAddonName(addon.tier) }} × {{ addon.quantity }}
                        </span>
                        <span class="font-medium text-secondary-700 dark:text-secondary-300">
                            {{ company.currency_symbol }}{{ (getAddonPrice(addon.tier) * addon.quantity).toFixed(2) }}
                        </span>
                    </div>
                </div>

                <!-- Total -->
                <div class="border-t border-secondary-200 dark:border-secondary-700 pt-4 mb-8">
                    <div class="flex justify-between items-center">
                        <span class="text-lg font-semibold text-secondary-900 dark:text-white">
                            Total (Monthly)
                        </span>
                        <span class="text-2xl font-bold text-primary-600">
                            {{ company.currency_symbol }}{{ totalPrice.toFixed(2) }}
                        </span>
                    </div>
                    <p class="text-xs text-secondary-600 dark:text-secondary-400 mt-2">
                        * Charged immediately upon reactivation
                    </p>
                </div>

                <!-- Reactivate Button -->
                <button
                    @click="handleReactivate"
                    :disabled="!selectedPlanId || processing"
                    class="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-secondary-400 disabled:to-secondary-500 text-white px-8 py-4 rounded-lg font-bold text-lg shadow-md hover:shadow-lg transition-all disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                    <svg v-if="!processing" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <svg v-else class="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{{ processing ? 'Processing...' : 'Reactivate Subscription' }}</span>
                </button>

                <p class="text-xs text-center text-secondary-500 dark:text-secondary-400 mt-4">
                    By reactivating, you agree to be charged {{ company.currency_symbol }}{{ totalPrice.toFixed(2) }} immediately and on a recurring monthly basis.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Link, router } from '@inertiajs/vue3';

// Props
const props = defineProps({
    company: {
        type: Object,
        required: true
    },
    previousSubscription: {
        type: Object,
        default: null
    },
    previousAddons: {
        type: Array,
        default: () => []
    },
    plans: {
        type: Array,
        required: true
    },
    storageAddons: {
        type: Array,
        required: true
    }
});

// State
const selectedPlanId = ref(props.previousSubscription?.plan_id || props.plans[0]?.id || null);
const selectedAddons = ref({});
const processing = ref(false);

// Initialize with previous add-ons if any
if (props.previousAddons.length > 0) {
    props.previousAddons.forEach(addon => {
        // Determine tier from storage_gb
        let tier = '';
        if (addon.storage_gb === 10) tier = '10gb';
        else if (addon.storage_gb === 50) tier = '50gb';
        else if (addon.storage_gb === 100) tier = '100gb';

        if (tier && addon.quantity) {
            selectedAddons.value[tier] = addon.quantity;
        }
    });
}

// Computed
const selectedPlan = computed(() => {
    return props.plans.find(p => p.id === selectedPlanId.value);
});

const selectedStorageAddons = computed(() => {
    return Object.entries(selectedAddons.value)
        .filter(([tier, quantity]) => quantity > 0)
        .map(([tier, quantity]) => ({ tier, quantity }));
});

const totalPrice = computed(() => {
    let total = selectedPlan.value?.price || 0;

    selectedStorageAddons.value.forEach(addon => {
        const addonPrice = getAddonPrice(addon.tier);
        total += addonPrice * addon.quantity;
    });

    return total;
});

// Methods
const getAddonQuantity = (tier) => {
    return selectedAddons.value[tier] || 0;
};

const incrementAddon = (tier) => {
    const current = selectedAddons.value[tier] || 0;
    if (current < 10) {
        selectedAddons.value[tier] = current + 1;
    }
};

const decrementAddon = (tier) => {
    const current = selectedAddons.value[tier] || 0;
    if (current > 0) {
        selectedAddons.value[tier] = current - 1;
    }
};

const getAddonPrice = (tier) => {
    const addon = props.storageAddons.find(a => a.tier === tier);
    return addon?.price || 0;
};

const getAddonName = (tier) => {
    const addon = props.storageAddons.find(a => a.tier === tier);
    return addon?.name || tier;
};

const hadPreviousAddon = (tier) => {
    return props.previousAddons.some(addon => {
        if (addon.storage_gb === 10 && tier === '10gb') return true;
        if (addon.storage_gb === 50 && tier === '50gb') return true;
        if (addon.storage_gb === 100 && tier === '100gb') return true;
        return false;
    });
};

const handleReactivate = () => {
    if (!selectedPlanId.value || processing.value) return;

    processing.value = true;

    // Prepare storage addons data
    const storageAddonsData = selectedStorageAddons.value.map(addon => ({
        tier: addon.tier,
        quantity: addon.quantity
    }));

    // Submit reactivation request
    router.post('/billing/reactivate', {
        company_id: props.company.id,
        plan_id: selectedPlanId.value,
        storage_addons: storageAddonsData.length > 0 ? storageAddonsData : null
    }, {
        onFinish: () => {
            processing.value = false;
        }
    });
};
</script>
